/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * EventDayPicker — which days of a multi-day event the board shows.
 *
 * A row of chips (All days, then one per day with its number of attached runs)
 * plus a calendar for picking a RANGE: the first click picks a day, a second
 * click on a later day stretches it into a range, and a click on an earlier day
 * starts again from there. Only the event's own days can be picked. Rendered
 * only on multi-day events — a one-day race board has no date picker at all.
 *
 * Controlled: `value` is { from, to } as event day numbers, both null for All
 * days, and every choice goes out through `onChange`.
 */

import React, {
  useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState,
} from 'react';
import { createPortal } from 'react-dom';
import { eventDayCount, eventDayDate } from './dailyMode';

const INK = '#241708';
const MUTED = '#6E6259';
const ACCENT = '#FF6A1A';
const ACCENT_INK = '#9A3412';
const HAIR = '#E6DED6';
const SOFT = '#FFF4EC';
const DAY_MS = 24 * 60 * 60 * 1000;
const CAL_WIDTH = 316;
const GUTTER = 16;
const GAP = 6;

const utcDate = (iso) => new Date(`${iso}T00:00:00Z`);
const fmt = (iso, opts) => utcDate(iso).toLocaleDateString('en-GB', { timeZone: 'UTC', ...opts });

function CalendarIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="5" width="18" height="16" rx="3" />
      <path d="M3 10h18M8 3v4M16 3v4" />
    </svg>
  );
}

function Chevron({ left }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
      strokeLinecap="round" aria-hidden="true">
      <path d={left ? 'm15 18-6-6 6-6' : 'm9 18 6-6-6-6'} />
    </svg>
  );
}

/** The months the event touches, as { year, month } (month 0-based). */
function monthsOf(startIso, endIso) {
  const out = [];
  const s = utcDate(startIso);
  const e = utcDate(endIso);
  let y = s.getUTCFullYear();
  let m = s.getUTCMonth();
  while (y < e.getUTCFullYear() || (y === e.getUTCFullYear() && m <= e.getUTCMonth())) {
    out.push({ year: y, month: m });
    m += 1;
    if (m > 11) { m = 0; y += 1; }
  }
  return out;
}

/**
 * Where the calendar goes, in viewport pixels, for a `position: fixed` popover
 * hung off the "Pick dates" button. Right-aligned under the button, kept inside
 * the screen sideways, and flipped above the button when there is more room
 * there than below. `maxHeight` lets it scroll rather than run off the screen.
 */
export function calendarPlacement(anchor, viewport, height) {
  const width = Math.min(CAL_WIDTH, viewport.width - GUTTER * 2);
  const left = Math.min(
    Math.max(anchor.right - width, GUTTER),
    Math.max(GUTTER, viewport.width - width - GUTTER),
  );
  const below = viewport.height - anchor.bottom - GAP - GUTTER;
  const above = anchor.top - GAP - GUTTER;
  if (height > below && above > below) {
    const top = Math.max(GUTTER, anchor.top - GAP - Math.min(height, above));
    return { top, left, width, maxHeight: Math.max(160, above) };
  }
  return { top: anchor.bottom + GAP, left, width, maxHeight: Math.max(160, below) };
}

/** "Recorded on Sat, 31 Oct 2026" / "Recorded 31 Oct – 2 Nov 2026 · 3 days". */
export function rangeCaption(value, startDatetime, last, timezone) {
  const startIso = eventDayDate(1, startDatetime, timezone);
  const endIso = eventDayDate(last, startDatetime, timezone);
  if (!value || value.from === null || value.from === undefined) {
    return `All ${last} days · ${fmt(startIso, { day: 'numeric', month: 'short' })} – ${fmt(endIso, { day: 'numeric', month: 'short', year: 'numeric' })}`;
  }
  const fromIso = eventDayDate(value.from, startDatetime, timezone);
  if (value.to === value.from) {
    return `Recorded on ${fmt(fromIso, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}`;
  }
  const toIso = eventDayDate(value.to, startDatetime, timezone);
  return `Recorded ${fmt(fromIso, { day: 'numeric', month: 'short' })} – ${fmt(toIso, { day: 'numeric', month: 'short', year: 'numeric' })} · ${value.to - value.from + 1} days`;
}

export default function EventDayPicker({
  startDatetime, endDatetime, timezone, counts, value, onChange,
}) {
  const last = eventDayCount(startDatetime, endDatetime, timezone);
  const startIso = eventDayDate(1, startDatetime, timezone);
  const endIso = eventDayDate(last, startDatetime, timezone);
  const months = useMemo(() => monthsOf(startIso, endIso), [startIso, endIso]);
  const [open, setOpen] = useState(false);
  const [monthIdx, setMonthIdx] = useState(0);
  // The first click of a range, waiting for its second.
  const [anchor, setAnchor] = useState(null);
  // The calendar lives in a portal on <body>, fixed to the button. Kept inside
  // the board it was clipped by the leaderboard card's `overflow: hidden` — on
  // a day with no activities the empty table left the card too short to hold
  // it, so it came out cut off with no Done button, hung under the caption bar
  // instead of the button.
  const buttonRef = useRef(null);
  const calRef = useRef(null);
  const [place, setPlace] = useState(null);

  const reposition = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const height = calRef.current ? calRef.current.offsetHeight : 420;
    setPlace(calendarPlacement(rect, { width: window.innerWidth, height: window.innerHeight }, height));
  }, []);

  useLayoutEffect(() => {
    if (!open) { setPlace(null); return undefined; }
    reposition();
    // Once more after the first paint, now its real height is known.
    const id = window.requestAnimationFrame(reposition);
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      window.cancelAnimationFrame(id);
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [open, monthIdx, reposition]);

  // A popover on <body> no longer closes by being clipped away: a click
  // outside it or Escape puts it away, like Done.
  useEffect(() => {
    if (!open) return undefined;
    function onDown(e) {
      if (calRef.current && calRef.current.contains(e.target)) return;
      if (buttonRef.current && buttonRef.current.contains(e.target)) return;
      setOpen(false);
    }
    function onKey(e) { if (e.key === 'Escape') setOpen(false); }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const from = value?.from ?? null;
  const to = value?.to ?? null;
  const all = from === null;
  const total = [...(counts || new Map()).values()].reduce((s, c) => s + (c.entries || 0), 0);

  function pickAll() { setAnchor(null); onChange({ from: null, to: null }); }
  function pickDay(n) { setAnchor(null); onChange({ from: n, to: n }); }

  function clickCell(n) {
    if (anchor !== null && n >= anchor) {
      onChange({ from: anchor, to: n });
      setAnchor(null);
      return;
    }
    // A first click, or an earlier day than the first: start again from here.
    setAnchor(n);
    onChange({ from: n, to: n });
  }

  const chipStyle = (on) => ({
    position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2,
    minWidth: 132, minHeight: 44, padding: '10px 16px 12px', borderRadius: 16, flexShrink: 0,
    border: `1px solid ${on ? INK : HAIR}`, background: on ? INK : '#fff',
    color: on ? '#E8C9B4' : MUTED, font: '600 12px system-ui, sans-serif', cursor: 'pointer', textAlign: 'left',
  });
  const bigStyle = (on) => ({
    fontFamily: 'Archivo, sans-serif', fontSize: 20, fontWeight: 800, letterSpacing: '-.02em', color: on ? '#fff' : INK,
  });
  const kicker = { fontSize: 11, fontWeight: 800, letterSpacing: '.1em', textTransform: 'uppercase' };

  const { year, month } = months[Math.min(monthIdx, months.length - 1)];
  const first = new Date(Date.UTC(year, month, 1));
  const lead = (first.getUTCDay() + 6) % 7;
  const inMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const startMs = utcDate(startIso).getTime();
  const cells = [];
  for (let i = 0; i < lead; i += 1) cells.push(null);
  for (let d = 1; d <= inMonth; d += 1) {
    const ms = Date.UTC(year, month, d);
    const n = Math.round((ms - startMs) / DAY_MS) + 1;
    const iso = new Date(ms).toISOString().slice(0, 10);
    cells.push({ d, n, iso, enabled: n >= 1 && n <= last });
  }

  return (
    <div style={{ position: 'relative', color: INK }}>
      <p id="event-day-label" style={{ ...kicker, margin: '0 0 10px', fontSize: 12, color: MUTED }}>Event day</p>
      <div style={{ display: 'flex', gap: 10, alignItems: 'stretch' }}>
        <div role="group" aria-labelledby="event-day-label"
          style={{ display: 'flex', gap: 10, overflowX: 'auto', flex: '1 1 auto', minWidth: 0, paddingBottom: 4 }}>
          <button type="button" aria-pressed={all ? 'true' : 'false'} onClick={pickAll} style={chipStyle(all)}>
            <span style={kicker}>Whole event</span>
            <span style={bigStyle(all)}>All days</span>
            <span>{total} activities</span>
          </button>
          {Array.from({ length: last }, (_, i) => i + 1).map((n) => {
            const iso = eventDayDate(n, startDatetime, timezone);
            const on = !all && from === n && to === n;
            const inRange = !all && n >= from && n <= to && from !== to;
            const entries = counts?.get(n)?.entries || 0;
            return (
              <button key={n} type="button" aria-pressed={on || inRange ? 'true' : 'false'}
                onClick={() => pickDay(n)}
                style={{ ...chipStyle(on), ...(inRange ? { borderColor: ACCENT, background: SOFT } : {}) }}>
                <span style={kicker}>{`Day ${n} · ${fmt(iso, { weekday: 'short' })}`}</span>
                <span style={bigStyle(on)}>{fmt(iso, { day: 'numeric', month: 'short' })}</span>
                <span>{entries ? `${entries} activities` : 'No activities'}</span>
              </button>
            );
          })}
        </div>
        <button ref={buttonRef} type="button" aria-expanded={open ? 'true' : 'false'} aria-controls="event-day-calendar"
          onClick={() => setOpen((v) => !v)}
          style={{
            display: 'inline-flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4,
            minHeight: 44, padding: '10px 16px', borderRadius: 12, border: `1px solid ${HAIR}`, background: '#fff',
            color: INK, font: '600 14px system-ui, sans-serif', cursor: 'pointer', flexShrink: 0,
          }}>
          <CalendarIcon />
          Pick dates
        </button>
      </div>

      {open && createPortal((
        <div ref={calRef} id="event-day-calendar" role="dialog" aria-label="Choose event days" style={{
          position: 'fixed', zIndex: 1000, color: INK,
          top: place ? place.top : 0, left: place ? place.left : 0,
          width: place ? place.width : CAL_WIDTH, maxWidth: `calc(100vw - ${GUTTER * 2}px)`,
          maxHeight: place ? place.maxHeight : undefined, overflowY: 'auto',
          // Hidden for the one frame before it is measured, so it never flashes at 0,0.
          visibility: place ? 'visible' : 'hidden',
          boxSizing: 'border-box', padding: 16, borderRadius: 20, background: '#fff', border: '1px solid #EEE8E2',
          boxShadow: '0 24px 48px -16px rgba(36,23,8,.35)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <button type="button" aria-label="Previous month" disabled={monthIdx === 0}
              onClick={() => setMonthIdx((i) => Math.max(0, i - 1))}
              style={{ minWidth: 36, height: 36, borderRadius: 10, border: `1px solid ${HAIR}`, background: '#fff', color: INK, opacity: monthIdx === 0 ? 0.4 : 1, cursor: 'pointer' }}>
              <Chevron left />
            </button>
            <p style={{ margin: 0, fontFamily: 'Archivo, sans-serif', fontSize: 16, fontWeight: 800 }}>
              {first.toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' })}
            </p>
            <button type="button" aria-label="Next month" disabled={monthIdx >= months.length - 1}
              onClick={() => setMonthIdx((i) => Math.min(months.length - 1, i + 1))}
              style={{ minWidth: 36, height: 36, borderRadius: 10, border: `1px solid ${HAIR}`, background: '#fff', color: INK, opacity: monthIdx >= months.length - 1 ? 0.4 : 1, cursor: 'pointer' }}>
              <Chevron />
            </button>
          </div>
          <div aria-hidden="true" style={{
            display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 4, marginTop: 12,
            fontSize: 11, fontWeight: 800, letterSpacing: '.06em', color: MUTED, textAlign: 'center',
          }}>
            {['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'].map((w) => <span key={w}>{w}</span>)}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 4, marginTop: 6 }}>
            {cells.map((c, i) => {
              if (!c) return <span key={`b${i}`} />;
              const selected = !all && c.n >= from && c.n <= to;
              return (
                <button key={c.iso} type="button" disabled={!c.enabled}
                  aria-label={fmt(c.iso, { day: 'numeric', month: 'long', year: 'numeric' })}
                  aria-pressed={selected ? 'true' : 'false'}
                  onClick={() => clickCell(c.n)}
                  style={{
                    height: 36, borderRadius: 10, border: 0, padding: 0, font: '600 13px system-ui, sans-serif',
                    background: selected ? ACCENT : (c.enabled ? SOFT : 'none'),
                    color: selected ? '#fff' : (c.enabled ? ACCENT_INK : '#A3968C'),
                    boxShadow: c.enabled && !selected ? 'inset 0 0 0 1px #FFD3B8' : 'none',
                    fontWeight: c.enabled ? 800 : 600, cursor: c.enabled ? 'pointer' : 'default',
                  }}>
                  {c.d}
                </button>
              );
            })}
          </div>
          <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid #EEE8E2', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 12, color: '#4A3D33' }}>
              {anchor !== null ? 'Pick an end day, or the same day again' : 'Pick a day, then an end day for a range'}
            </span>
            <button type="button" onClick={() => setOpen(false)}
              style={{ height: 36, padding: '0 14px', borderRadius: 10, border: `1px solid ${HAIR}`, background: '#fff', color: INK, font: '600 13px system-ui, sans-serif', cursor: 'pointer' }}>
              Done
            </button>
          </div>
        </div>
      ), document.body)}

      <div style={{
        marginTop: 12, padding: '10px 14px', borderRadius: 12, background: SOFT,
        display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', fontSize: 14, color: '#4A3D33',
      }}>
        <span style={{ flex: '1 1 auto', minWidth: 0 }} aria-live="polite">
          <strong style={{ color: INK }}>{rangeCaption(value, startDatetime, last, timezone)}</strong>
        </span>
        {!all && (
          <button type="button" onClick={pickAll}
            style={{ height: 34, padding: '0 14px', borderRadius: 10, border: `1px solid ${HAIR}`, background: '#fff', color: INK, font: '600 13px system-ui, sans-serif', cursor: 'pointer' }}>
            Show all days
          </button>
        )}
      </div>
    </div>
  );
}
